import { readEnvFile } from '../utils/envManager';

interface SearchResult {
  title: string;
  url: string;
  description: string;
}

interface SearchOptions {
  language?: 'en' | 'es';
  country?: string;
}

// Default country codes for each language
const DEFAULT_COUNTRIES = {
  en: 'US',
  es: 'ES'
} as const;

interface BraveSearchResponse {
  web: {
    results: Array<{
      title: string;
      url: string;
      description: string;
    }>;
  };
}

export class BraveSearchService {
  private static instance: BraveSearchService;
  private readonly API_KEY: string;
  private readonly BASE_URL = 'https://anthropic-proxy.camaleon.ai/api/brave-search';
  private cache: Map<string, { results: SearchResult[]; timestamp: number }>;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private rateLimitQueue: Promise<void>;

  private constructor() {
    // Try local env file first, then fallback to Vite env (for Cloudflare)
    const envVars = readEnvFile();
    const localApiKey = envVars['SYSTEM_BRAVE_API_KEY'];
    const viteApiKey = import.meta.env.SYSTEM_BRAVE_API_KEY;
    
    console.log('API Key sources:', {
      fromLocalEnv: localApiKey ? '[PRESENT]' : '[MISSING]',
      fromViteEnv: viteApiKey ? '[PRESENT]' : '[MISSING]'
    });

    this.API_KEY = localApiKey || viteApiKey || '';
    
    if (!this.API_KEY) {
      console.error('Brave Search API key not found in any environment source');
    } else {
      console.log('Brave Search API key loaded successfully from:', localApiKey ? 'local env' : 'Vite env');
    }
    this.cache = new Map();
    this.rateLimitQueue = Promise.resolve();
  }

  public static getInstance(): BraveSearchService {
    if (!BraveSearchService.instance) {
      BraveSearchService.instance = new BraveSearchService();
    }
    return BraveSearchService.instance;
  }

  private async rateLimit(): Promise<void> {
    // Simple rate limiting using a queue
    this.rateLimitQueue = this.rateLimitQueue.then(() => 
      new Promise(resolve => setTimeout(resolve, 100)) // 100ms delay between requests
    );
    await this.rateLimitQueue;
  }

  private getCacheKey(query: string, options?: SearchOptions): string {
    const lang = options?.language || 'en';
    const country = options?.country || DEFAULT_COUNTRIES[lang as keyof typeof DEFAULT_COUNTRIES] || 'US';
    return `${query}:${lang}:${country}`;
  }

  private getCachedResults(query: string, options?: SearchOptions): SearchResult[] | null {
    const cacheKey = this.getCacheKey(query, options);
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.results;
    }
    return null;
  }

  private setCachedResults(query: string, results: SearchResult[], options?: SearchOptions): void {
    const cacheKey = this.getCacheKey(query, options);
    this.cache.set(cacheKey, {
      results,
      timestamp: Date.now()
    });
  }

  public async search(query: string, options?: SearchOptions): Promise<SearchResult[]> {
    if (!this.API_KEY || this.API_KEY.trim() === '') {
      console.error('API Key validation failed:', {
        apiKeyExists: !!this.API_KEY,
        apiKeyLength: this.API_KEY?.length || 0,
        isEmptyString: this.API_KEY === '',
        isWhitespace: this.API_KEY?.trim() === ''
      });
      throw new Error('Brave Search API key not configured or invalid');
    }

    // Check cache first
    const cachedResults = this.getCachedResults(query);
    if (cachedResults) {
      return cachedResults;
    }

    // Apply rate limiting
    await this.rateLimit();

    try {
      const searchOptions = options || {};
      const lang = searchOptions.language || 'en';
      const country = searchOptions.country || DEFAULT_COUNTRIES[lang as keyof typeof DEFAULT_COUNTRIES] || 'US';
      
      console.log('Starting search with query:', query, 'language:', lang, 'country:', country);
      console.log('Using API key:', this.API_KEY ? '[PRESENT]' : '[MISSING]');
      
      const params = new URLSearchParams({
        q: query,
        count: '5',
        search_lang: lang,
        country: country
      });

      const url = `${this.BASE_URL}?${params.toString()}`;
      console.log('Request URL:', url);

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Subscription-Token': this.API_KEY,
          ...(searchOptions.country && { 'X-Loc-Country': searchOptions.country })
        }
      }).catch(error => {
        console.error('Network error details:', {
          error: error.message,
          type: error.name,
          stack: error.stack
        });
        throw new Error(`Network request failed: ${error.message}`);
      });

      console.log('Response status:', response.status);

      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('Raw response:', responseText.substring(0, 200) + '...');

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (error) {
        console.error('Failed to parse response as JSON:', error);
        throw new Error('Invalid JSON response from server. Received: ' + responseText.substring(0, 100) + '...');
      }

      if (!response.ok) {
        console.error('API Error Response:', data);
        
        if (response.status === 401) {
          throw new Error('Invalid API key or unauthorized access');
        }
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      if (!data || !data.web || !Array.isArray(data.web.results)) {
        console.error('Unexpected API response format:', data);
        throw new Error('Invalid response format from search API');
      }

      const results = data.web.results
        .filter((result: { title?: string; url?: string; description?: string }) =>
          result.title && result.url && result.description)
        .map((result: { title: string; url: string; description: string }) => ({
          title: result.title,
          url: result.url,
          description: result.description,
        }));

      if (results.length === 0) {
        console.warn('No valid search results found');
        return [];
      }

      // Cache the results
      this.setCachedResults(query, results);

      return results;
    } catch (error) {
      console.error('Error performing search:', error);
      throw error;
    }
  }

  public clearCache(): void {
    this.cache.clear();
  }
}

export const braveSearchService = BraveSearchService.getInstance();